import React, { FC, useContext, useEffect, useState } from 'react'
import { Trans, t } from '@lingui/macro'

import TableExtendedOld from '../../../../components/common/table-extended-old/TableExtendedOld'

import { Column } from 'react-table'

import Tag from '../../../../components/common/tag/Tag'
import Tooltip from '../../../../components/common/tooltip/Tooltip'
import { ManageCampaignContext } from '../../ManageCampaignPage'
import { NextActionColumn } from './NextActionColumn'
import { formatDistanceToNow } from 'date-fns'
import { LinkableUserAvatar } from '../../../../components/common/user/linkable-user-avatar/LinkableUserAvatar'
import Spinner, {
  SpinnerSizeEnum
} from '../../../../components/common/spinner/Spinner'
import {
  CampaignAudienceTableOrderColumn,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useCampaignAudienceTableQuery } from './CampaignAudienceTable.codegen'
import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'
import { Box, Text } from 'front-end/src/components'

interface IProps {
  campaignId: number
}

const formatRowWithDate = (row) =>
  row.value && formatDateAndTimeShort(row.value)

const CampaignAudienceTable: FC<IProps> = (props) => {
  const {
    campaign,
    refetchCampaign,
    loading: loadingCampaign
  } = useContext(ManageCampaignContext)

  const { campaignId } = props
  const [tableFilters, setTableFilters] = useState({
    page: 1,
    limit: 20,
    orderColumn: CampaignAudienceTableOrderColumn.last_join_timestamp,
    orderDirection: SqlOrderDirectionEnum.DESC,
    search: ''
  })

  const { data, refetch } = useCampaignAudienceTableQuery({
    variables: {
      id: campaignId,
      offset: (tableFilters.page - 1) * tableFilters.limit,
      ...tableFilters
    }
  })
  useEffect(() => {
    refetch() // this is needed for when the campaign is activated-the audience get's populated on the backend and we need to refetch
  }, [campaign.is_active])

  const tableColumns: Column[] = [
    {
      Header: '',
      sortable: false,
      maxWidth: 54,
      Cell: (row) => {
        return <LinkableUserAvatar user={row.original} />
      }
    },
    {
      id: CampaignAudienceTableOrderColumn.display_name,
      Header: t`Name`,
      Cell: (row) => (
        <Text className="dark" noOfLines={2} as="strong">
          <Tooltip
            text={row.original.email}
            display="block"
            dataTest="AudiencePersonName"
          >
            {row.original.display_name}
          </Tooltip>
        </Text>
      )
    },
    {
      Header: t`Joined`,
      maxWidth: 180,
      accessor: 'lastJoinTimestamp',
      Cell: formatRowWithDate
    },
    {
      Header: t`Actions`,
      maxWidth: 100,
      sortable: false,
      className: 'blueText',
      accessor: 'scheduledActionsCountUntilNow'
    },
    {
      Header: t`Engagement`,
      maxWidth: 100,
      sortable: false,
      className: 'greenText',
      accessor: 'analyticEventsCount'
    },
    {
      Header: t`Next action`,
      sortable: false,
      maxWidth: 250,
      accessor: 'scheduledActions',
      Cell: (row) => {
        if (row.value.length === 0) {
          return null
        }
        return <NextActionColumn campaignId={campaignId} {...row.value[0]} />
      }
    },
    {
      Header: t`Status`,
      sortable: false,
      accessor: 'status',
      minWidth: 65,
      Cell: (row) => {
        return (
          <span className="green">
            {row.original.completedTimestamp ? (
              <Tag color="green">
                <Trans>Completed</Trans>
              </Tag>
            ) : (
              <Tag color="blue">
                <Trans>Active</Trans>
              </Tag>
            )}
          </span>
        )
      }
    }
  ]

  let pagesCount
  const admin = data?.admin
  if (admin && admin.campaign) {
    pagesCount = Math.ceil(
      (admin.campaign?.audienceEventsCount ?? 0) / tableFilters.limit
    )
  } else {
    pagesCount = 1
  }
  const isActiveWithNoAudienceCheck =
    !campaign.last_completed_audience_check && campaign.is_active

  const audienceInfo = () => {
    if (isActiveWithNoAudienceCheck && !loadingCampaign) {
      return (
        <div className="topic-completion-alert">
          <Trans>
            Campaign audience is still populating,{' '}
            <a
              onClick={() => {
                refetchCampaign()
              }}
            >
              refresh
            </a>{' '}
            in a minute or two.
          </Trans>
        </div>
      )
    }
  }

  const sortColumn =
    tableFilters.orderColumn ===
    CampaignAudienceTableOrderColumn.last_join_timestamp
      ? 'lastJoinTimestamp'
      : tableFilters.orderColumn

  return (
    <div className="CampaignAudience__table">
      {isActiveWithNoAudienceCheck && loadingCampaign && (
        <Spinner size={SpinnerSizeEnum.small}> </Spinner>
      )}
      {audienceInfo()}
      <Box mt="16px">
        <TableExtendedOld
          loading={loadingCampaign}
          manual
          data={admin && admin.campaign && admin.campaign.audienceEvents}
          tableHeader={t`Audience`}
          columns={tableColumns}
          className="-highlight AnalyticsTable"
          defaultPageSize={tableFilters.limit}
          showPageSizeOptions={false}
          showSearchFilter
          resizable={false}
          minRows={5}
          pages={pagesCount}
          defaultSortDesc
          defaultSorted={[
            {
              id: sortColumn,
              desc: tableFilters.orderDirection === SqlOrderDirectionEnum.DESC
            }
          ]}
          onSortedChange={(newSorted) => {
            setTableFilters((prevState) => {
              const descendingOrder = newSorted[0] ? newSorted[0].desc : false
              let orderColumn = newSorted[0]
                ? newSorted[0].id
                : CampaignAudienceTableOrderColumn.display_name

              if (orderColumn === 'lastJoinTimestamp') {
                orderColumn =
                  CampaignAudienceTableOrderColumn.last_join_timestamp
              }

              return {
                ...prevState,
                orderColumn: orderColumn as CampaignAudienceTableOrderColumn,
                orderDirection: descendingOrder
                  ? SqlOrderDirectionEnum.DESC
                  : SqlOrderDirectionEnum.ASC
              }
            })
          }}
          onPageChange={(newPage) => {
            setTableFilters((prevState) => {
              return {
                ...prevState,
                page: newPage + 1
              }
            })
          }}
          page={tableFilters.page - 1}
          onSearchChange={(searchVal) => {
            setTableFilters((prevState) => {
              return {
                ...prevState,
                search: searchVal
              }
            })
          }}
        />
      </Box>
      {campaign.last_completed_audience_check && (
        <span>
          <Trans>
            Last audience update ran{' '}
            {formatDistanceToNow(
              new Date(campaign.last_completed_audience_check)
            )}{' '}
            ago.
          </Trans>
        </span>
      )}
    </div>
  )
}

export default CampaignAudienceTable
